import React, { useRef } from "react";
import {
    Box,
    Flex,
    Text,
    Link,
    useColorModeValue,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Phone, MapPin, Mail } from "lucide-react";
import { motion, useInView } from "framer-motion";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

interface ContactItem {
    id: string;
    icon: React.ReactNode;
    label: string;
    value: string;
    isLink?: boolean;
    href?: string;
    accent: string;
    enterFrom: "left" | "bottom" | "right";
}

// ─── Carte individuelle ────────────────────────────────────────────────────────
const ContactCard: React.FC<{ item: ContactItem; index: number; isInView: boolean }> = ({
    item,
    index,
    isInView,
}) => {
    const textColor = useColorModeValue("#0A1628", "#E8E0D0");
    const cardBg = useColorModeValue("rgba(255,255,255,0.65)", "rgba(255,255,255,0.03)");
    const cardBorder = useColorModeValue("rgba(10,22,40,0.07)", "rgba(255,255,255,0.06)");

    const directions = {
        left:   { x: -60, y: 0 },
        bottom: { x: 0,   y: 60 },
        right:  { x: 60,  y: 0 },
    };

    const cardVariants = {
        hidden: {
            opacity: 0,
            x: directions[item.enterFrom].x,
            y: directions[item.enterFrom].y,
            scale: 0.95,
            filter: "blur(6px)",
        },
        visible: {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            transition: {
                duration: 0.9,
                delay: index * 0.14,
                ease: [0.16, 1, 0.3, 1],
            },
        },
    };

    return (
        <MotionFlex
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            whileHover={{
                y: -10,
                scale: 1.02,
                transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
            }}
            direction="column"
            align="center"
            justify="center"
            w="100%"
            h="100%"
            p={{ base: 10, md: 14 }}
            border="1px solid"
            borderColor={cardBorder}
            borderRadius="28px"
            bg={cardBg}
            backdropFilter="blur(20px) saturate(180%)"
            boxShadow="0 1px 0 rgba(255,255,255,0.6) inset, 0 32px 64px rgba(0,0,0,0.06)"
            position="relative"
            overflow="hidden"
            cursor="default"
            gap={0}
        >
            {/* Ligne d'accent en haut */}
            <Box
                position="absolute"
                top={0}
                left="15%"
                right="15%"
                h="1px"
                bg={`linear-gradient(90deg, transparent, ${item.accent}90, transparent)`}
            />

            {/* Halo coloré derrière l'icône */}
            <Box
                position="absolute"
                top="-40px"
                left="50%"
                transform="translateX(-50%)"
                w="120px"
                h="120px"
                borderRadius="full"
                bg={`radial-gradient(ellipse, ${item.accent}18 0%, transparent 70%)`}
                pointerEvents="none"
            />

            {/* Icône */}
            <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.6, opacity: 0 }}
                transition={{ duration: 0.7, delay: index * 0.14 + 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
                <Box
                    color={item.accent}
                    mb={7}
                    opacity={0.9}
                    p={4}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor={`${item.accent}25`}
                    bg={`${item.accent}08`}
                >
                    {item.icon}
                </Box>
            </motion.div>

            {/* Label */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ duration: 0.6, delay: index * 0.14 + 0.42, ease: [0.16, 1, 0.3, 1] }}
            >
                <Text
                    fontSize="9px"
                    letterSpacing="0.35em"
                    textTransform="uppercase"
                    color={textColor}
                    opacity={0.35}
                    mb={3}
                    fontWeight="600"
                >
                    {item.label}
                </Text>
            </motion.div>

            {/* Séparateur */}
            <motion.div
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 0.6, delay: index * 0.14 + 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ originX: 0.5, marginBottom: "0.75rem" }}
            >
                <Box
                    w="28px"
                    h="1px"
                    bg={item.accent}
                    opacity={0.5}
                    mx="auto"
                />
            </motion.div>

            {/* Valeur */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                transition={{ duration: 0.6, delay: index * 0.14 + 0.55, ease: [0.16, 1, 0.3, 1] }}
            >
                {item.isLink ? (
                    <Link
                        href={item.href}
                        fontSize={{ base: "md", md: "lg" }}
                        color={textColor}
                        fontWeight="300"
                        opacity={0.75}
                        letterSpacing="0.01em"
                        _hover={{ opacity: 1, textDecoration: "none" }}
                        transition="opacity 0.3s"
                    >
                        {item.value}
                    </Link>
                ) : (
                    <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color={textColor}
                        fontWeight="300"
                        opacity={0.75}
                        letterSpacing="0.01em"
                        textAlign="center"
                    >
                        {item.value}
                    </Text>
                )}
            </motion.div>
        </MotionFlex>
    );
};

// ─── Composant principal ───────────────────────────────────────────────────────
const Contact: React.FC = () => {
    const { t } = useTranslation();
    const ref = useRef(null);

    // once: false → animation à l'entrée ET reset à la sortie
    const isInView = useInView(ref, { once: false, margin: "-100px" });

    const bg = useColorModeValue("#FAFAF7", "#0A1628");
    const textColor = useColorModeValue("#0A1628", "#E8E0D0");

    const contacts: ContactItem[] = [
        {
            id: "phone",
            icon: <Phone size={36} strokeWidth={1} />,
            label: t("contactTet"),
            value: t("contactTelnum"),
            isLink: true,
            href: `tel:${t("contactTelnum")}`,
            accent: "#C9A84C",
            enterFrom: "left",
        },
        {
            id: "address",
            icon: <MapPin size={36} strokeWidth={1} />,
            label: t("contactadress"),
            value: t("contactadressnum"),
            accent: "#8B9E6E",
            enterFrom: "bottom",
        },
        {
            id: "mail",
            icon: <Mail size={36} strokeWidth={1} />,
            label: t("contactmail"),
            value: t("contactmailnum"),
            accent: "#7BA3C0",
            enterFrom: "right",
        },
    ];

    return (
        <Box
            id="contact"
            ref={ref}
            py={{ base: 24, md: 40 }}
            px={{ base: 8, md: 16 }}
            textAlign="center"
            bg={bg}
            position="relative"
            overflow="hidden"
        >
            {/* Cercles décoratifs en fond */}
            <Box
                position="absolute"
                top="-100px"
                right="-100px"
                w="350px"
                h="350px"
                borderRadius="full"
                border="1px solid"
                borderColor={useColorModeValue("rgba(10,22,40,0.04)", "rgba(255,255,255,0.03)")}
                pointerEvents="none"
            />
            <Box
                position="absolute"
                bottom="-60px"
                left="-60px"
                w="240px"
                h="240px"
                borderRadius="full"
                border="1px solid"
                borderColor={useColorModeValue("rgba(10,22,40,0.03)", "rgba(255,255,255,0.02)")}
                pointerEvents="none"
            />

            {/* ── Header ── */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
                <Text
                    fontSize="10px"
                    letterSpacing="0.4em"
                    textTransform="uppercase"
                    color={textColor}
                    opacity={0.3}
                    mb={5}
                    fontWeight="500"
                >
                    {t("contactTitre")}
                </Text>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 36 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 36 }}
                transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
                <Text
                    fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                    fontWeight="200"
                    color={textColor}
                    letterSpacing="-0.035em"
                    lineHeight="1.05"
                    mb={5}
                >
                    {t("contactHeading1")}{" "}
                    <Box as="span" fontWeight="800">
                        {t("contactHeading2")}
                    </Box>
                </Text>
            </motion.div>

            {/* Séparateur dégradé animé */}
            <motion.div
                initial={{ scaleX: 0, opacity: 0 }}
                animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
                transition={{ duration: 0.9, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
                style={{ originX: 0.5, marginBottom: "5rem" }}
            >
                <Box
                    w="64px"
                    h="1px"
                    bg="linear-gradient(90deg, #C9A84C, #8B9E6E)"
                    mx="auto"
                    opacity={0.7}
                />
            </motion.div>

            {/* ── Cartes ── */}
            <Flex
                direction={{ base: "column", md: "row" }}
                gap={6}
                maxW="1100px"
                mx="auto"
                align="stretch"
            >
                {contacts.map((c, i) => (
                    <Box key={c.id} flex="1" display="flex">
                        <ContactCard item={c} index={i} isInView={isInView} />
                    </Box>
                ))}
            </Flex>
        </Box>
    );
};

export default Contact;